import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  getPortalStudents, getPortalServices, createPortalService, updatePortalService, deletePortalService,
  exportPortalServices,
} from "../../lib/api";

const EMPTY = { student_id: "", service_provided: "", group_size_abbr: "G", minutes_for_x: 30, diagnostic_code: "", max_per_week: "" };
const KNOWN_GROUPS = ["G", "SG"];

export default function PortalBilling() {
  const [students, setStudents] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [groupMode, setGroupMode] = useState("G"); // "G" | "SG" | "__other__"
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, sv] = await Promise.all([getPortalStudents(), getPortalServices()]);
      setStudents([...(st.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setServices(sv.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load billing data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, student_id: students[0]?.id || "" });
    setGroupMode("G");
    setDialogOpen(true);
  };

  const openEdit = (svc) => {
    setEditing(svc);
    const known = KNOWN_GROUPS.includes(svc.group_size_abbr);
    setGroupMode(known ? svc.group_size_abbr : "__other__");
    setForm({
      student_id: svc.student_id,
      service_provided: svc.service_provided || "",
      group_size_abbr: svc.group_size_abbr || "",
      minutes_for_x: svc.minutes_for_x ?? 30,
      diagnostic_code: svc.diagnostic_code || "",
      max_per_week: svc.max_per_week ?? "",
    });
    setDialogOpen(true);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.student_id) { toast.error("Select a student"); return; }
    if (!form.service_provided.trim()) { toast.error("Enter the service (be specific)"); return; }
    setSaving(true);
    const payload = {
      student_id: form.student_id,
      service_provided: form.service_provided.trim(),
      group_size_abbr: groupMode === "__other__" ? (form.group_size_abbr || "").trim() : groupMode,
      minutes_for_x: parseInt(form.minutes_for_x, 10) || 0,
      diagnostic_code: (form.diagnostic_code || "").trim(),
      max_per_week: form.max_per_week === "" || form.max_per_week == null ? null : parseInt(form.max_per_week, 10),
    };
    try {
      if (editing) {
        await updatePortalService(editing.id, payload);
        toast.success("Service updated");
      } else {
        await createPortalService(payload);
        toast.success("Service logged");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.service_provided}" for ${svc.student_name}?`)) return;
    try {
      await deletePortalService(svc.id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await exportPortalServices();
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "DAT_Log.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to export");
    }
  };

  return (
    <div className="space-y-4" data-testid="portal-billing">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        <strong>DAT Log</strong> — add one entry per service you want reported for a student. Enter the service, group
        size, minutes (=X), diagnostic code, and (if there&apos;s a limit) the max times per week. The school handles the
        rest of the Medicaid billing.
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{services.length} service{services.length === 1 ? "" : "s"} on file</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportXlsx} disabled={services.length === 0} data-testid="portal-billing-export">
            Export (Excel)
          </Button>
          <Button onClick={openAdd} disabled={students.length === 0} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="portal-billing-add">
            + Log a Service
          </Button>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : services.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="portal-billing-empty">No services logged yet. Click &quot;Log a Service&quot; to add one.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="portal-billing-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-3">Student</th>
                  <th className="p-3">Service</th>
                  <th className="p-3 text-center">Group</th>
                  <th className="p-3 text-center">Minutes (X)</th>
                  <th className="p-3">Diagnostic Code</th>
                  <th className="p-3 text-center">Max / Week</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <tr key={svc.id} className="border-b hover:bg-slate-50" data-testid={`portal-billing-row-${svc.id}`}>
                    <td className="p-3 font-medium whitespace-nowrap">{svc.student_name}</td>
                    <td className="p-3">{svc.service_provided}</td>
                    <td className="p-3 text-center"><Badge variant="outline">{svc.group_size_abbr || "—"}</Badge></td>
                    <td className="p-3 text-center">{svc.minutes_for_x}</td>
                    <td className="p-3">{svc.diagnostic_code || "—"}</td>
                    <td className="p-3 text-center">{svc.max_per_week ?? "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openEdit(svc)} data-testid={`portal-billing-edit-${svc.id}`}>Edit</Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => remove(svc)} data-testid={`portal-billing-del-${svc.id}`}>Del</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Service" : "Log a Service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Student *</Label>
              <select value={form.student_id} onChange={(e) => set("student_id", e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="portal-billing-student">
                <option value="">Select a student…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Service to be Provided (be specific — ONE service) *</Label>
              <Input value={form.service_provided} onChange={(e) => set("service_provided", e.target.value)} placeholder="e.g. Individual Speech Therapy" data-testid="portal-billing-service" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Group or Small Group</Label>
                <select value={groupMode} onChange={(e) => setGroupMode(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="portal-billing-group">
                  <option value="G">G (Group)</option>
                  <option value="SG">SG (Small Group)</option>
                  <option value="__other__">Other…</option>
                </select>
                {groupMode === "__other__" && (
                  <Input value={form.group_size_abbr} onChange={(e) => set("group_size_abbr", e.target.value)} placeholder="Specify" className="mt-1" data-testid="portal-billing-group-other" />
                )}
              </div>
              <div className="space-y-2">
                <Label>Number of Minutes (= X)</Label>
                <Input type="number" min="0" value={form.minutes_for_x} onChange={(e) => set("minutes_for_x", e.target.value)} data-testid="portal-billing-minutes" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Diagnostic Code</Label>
                <Input value={form.diagnostic_code} onChange={(e) => set("diagnostic_code", e.target.value)} placeholder="e.g. F80.9" data-testid="portal-billing-diag" />
              </div>
              <div className="space-y-2">
                <Label>Max Times per Week (if limit)</Label>
                <Input type="number" min="0" value={form.max_per_week} onChange={(e) => set("max_per_week", e.target.value)} placeholder="Leave blank if none" data-testid="portal-billing-max" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="portal-billing-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
